import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  FiTrendingUp, FiArrowUpRight, FiArrowDownLeft,
  FiArrowRight, FiClock, FiActivity, FiShoppingBag,
  FiDollarSign, FiPieChart, FiX, FiCheck, FiAward, FiShield, FiHome, FiEye
} from "react-icons/fi";
import { FaWallet } from "react-icons/fa";
import { GiMoneyStack, GiTakeMyMoney } from "react-icons/gi";
import { MdAccountBalanceWallet } from "react-icons/md";
import Invoice from "../../components/Invoice";

/* ═══════════════════════════════════════════
   VIBRANT NEUROMORPHISM STYLES — Consistent with PU&AP & LBBP Dashboards
   Blue · Green · Purple · Amber · Red palette
═══════════════════════════════════════════ */
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800;900&display=swap');

  .mem-root {
    font-family: 'Montserrat', sans-serif;
    background: linear-gradient(135deg, #e0e5ec 0%, #d1d6df 100%);
    min-height: 100%;
    padding: 6px 5px;
  }
  .mem-root *, .mem-root *::before, .mem-root *::after { box-sizing: border-box; }
  
  .mem-root ::-webkit-scrollbar { width: 6px; height: 6px; }
  .mem-root ::-webkit-scrollbar-track { 
    background: #d1d6df;
    border-radius: 99px;
    box-shadow: inset 0 1px 3px rgba(0,0,0,.1);
  }
  .mem-root ::-webkit-scrollbar-thumb { 
    background: linear-gradient(135deg, #2563eb, #8b5cf6);
    border-radius: 99px;
    box-shadow: inset 0 1px 2px rgba(255,255,255,.3), inset 0 -1px 2px rgba(0,0,0,.1);
  }

  .mem-page {
    max-width: 1280px;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    gap: 28px;
  }
  @media (max-width: 600px) { .mem-page { gap: 20px; } }

  /* ══════════════════════════════
     HERO BANNER — Vibrant Gradient
  ══════════════════════════════ */
  .mem-hero {
    background: linear-gradient(135deg, #0a1628 0%, #1e3a5f 50%, #2563eb 100%);
    border-radius: 9px;
    padding: 18px 20px;
    position: relative;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
    box-shadow: 8px 8px 16px #8f95a0, -8px -8px 16px #ffffff;
    border: 1px solid rgba(255,255,255,0.16);
  }
  .mem-hero::before {
    content: '';
    position: absolute;
    top: -22%;
    right: -8%;
    width: 240px;
    height: 240px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(59,130,246,.2) 0%, transparent 70%);
    pointer-events: none;
  }
  .mem-hero::after {
    content: '';
    position: absolute;
    bottom: -22%;
    left: -8%;
    width: 220px;
    height: 220px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(139,92,246,.15) 0%, transparent 70%);
    pointer-events: none;
  }

  .mem-hero-left { position: relative; z-index: 1; }
  .mem-hero-greeting {
    font-size: 11px;
    font-weight: 700;
    color: rgba(255,255,255,0.8);
    text-transform: uppercase;
    letter-spacing: 0.12em;
    margin-bottom: 8px;
  }
  .mem-hero-name {
    font-size: clamp(18px, 3.5vw, 24px);
    font-weight: 700;
    color: #ffffff;
    line-height: 1.2;
    margin-bottom: 6px;
    text-shadow: 1px 1px 2px rgba(0,0,0,0.2);
  }
  .mem-hero-sub {
    font-size: 12px;
    font-weight: 500;
    color: rgba(255,255,255,0.82);
  }
  .mem-hero-badges {
    display: flex;
    gap: 10px;
    margin-top: 12px;
    flex-wrap: nowrap;
    align-items: center;
  }
  
  /* Membership Badges */
  .mem-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 5px 12px;
    border-radius: 9px;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    background: rgba(255,255,255,0.1);
    backdrop-filter: blur(8px);
    border: 1px solid rgba(255,255,255,0.2);
  }
  .mem-badge.amber { color: #4ade80; }
  .mem-badge.amber2 { color: #f87171; }
  .mem-badge.blue { color: #93c5fd; }
  .mem-badge-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: currentColor;
    animation: mem-pulse 2s infinite;
    box-shadow: 0 0 8px currentColor;
  }
  @keyframes mem-pulse {
    0%,100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.4; transform: scale(1.2); }
  }

  .mem-hero-stats {
    display: flex;
    gap: 12px;
    position: relative;
    z-index: 1;
    flex-wrap: wrap;
  }
  .mem-hero-stat {
    background: rgba(255,255,255,0.1);
    backdrop-filter: blur(8px);
    border: 1px solid rgba(255,255,255,0.2);
    border-radius: 9px;
    padding: 10px 14px;
    text-align: center;
    min-width: 100px;
    transition: all 0.2s;
    box-shadow: 4px 4px 8px rgba(0,0,0,.1), -2px -2px 8px rgba(255,255,255,.1);
  }
  .mem-hero-stat:hover {
    transform: translateY(-3px);
    background: rgba(255,255,255,0.2);
  }
  .mem-hero-stat-val {
    font-size: clamp(15px, 2.5vw, 20px);
    font-weight: 700;
    background: linear-gradient(135deg, #fbbf24, #f59e0b);
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
  .mem-hero-stat-label {
    font-size: 9px;
    font-weight: 800;
    color: rgba(255,255,255,0.78);
    text-transform: uppercase;
    letter-spacing: 0.08em;
    margin-top: 4px;
  }

  /* ══════════════════════════════
     SECTION LABEL
  ══════════════════════════════ */
  .mem-section-label {
    font-size: 12px;
    font-weight: 800;
    background: linear-gradient(135deg, #2563eb, #8b5cf6);
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    display: flex;
    align-items: center;
    gap: 12px;
  }
  .mem-section-label::after {
    content: '';
    flex: 1;
    height: 2px;
    background: linear-gradient(90deg, #2563eb, #8b5cf6, transparent);
    border-radius: 99px;
  }

  /* ══════════════════════════════
     WALLET GRID — Neumorphism Cards
  ══════════════════════════════ */
  .mem-wallet-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
  }
  @media (max-width: 900px) { .mem-wallet-grid { grid-template-columns: repeat(2, 1fr); } }
  @media (max-width: 500px) { .mem-wallet-grid { grid-template-columns: 1fr; } }

  .mem-wallet-card {
    background: linear-gradient(145deg, #e8edf5, #dce1e8);
    border-radius: 9px;
    padding: 16px;
    box-shadow: 8px 8px 16px #8f95a0, -6px -6px 12px #ffffff;
    position: relative;
    overflow: hidden;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
  .mem-wallet-card:hover {
    transform: translateY(-3px);
    box-shadow: 12px 12px 24px #8f95a0, -8px -8px 14px #ffffff;
  }
  .mem-wallet-card::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    border-radius: 22px 22px 0 0;
  }
  .mem-wallet-card.blue::before   { background: linear-gradient(90deg, #3b82f6, #60a5fa); }
  .mem-wallet-card.green::before  { background: linear-gradient(90deg, #10b981, #34d399); }
  .mem-wallet-card.purple::before { background: linear-gradient(90deg, #8b5cf6, #a78bfa); }

  .mem-wallet-top {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 12px;
  }
  .mem-wallet-icon-wrap {
    width: 40px;
    height: 40px;
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    box-shadow: inset -2px -2px 5px rgba(255,255,255,0.7), inset 2px 2px 5px rgba(0,0,0,0.08);
  }
  .mem-wallet-card.blue .mem-wallet-icon-wrap   { background: linear-gradient(145deg, #dbeafe, #eff6ff); color: #3b82f6; }
  .mem-wallet-card.green .mem-wallet-icon-wrap  { background: linear-gradient(145deg, #dcfce7, #f0fdf4); color: #10b981; }
  .mem-wallet-card.purple .mem-wallet-icon-wrap { background: linear-gradient(145deg, #ede9fe, #f5f3ff); color: #8b5cf6; }

  .mem-wallet-label {
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    margin-bottom: 5px;
  }
  .mem-wallet-card.blue .mem-wallet-label   { color: #3b82f6; }
  .mem-wallet-card.green .mem-wallet-label  { color: #10b981; }
  .mem-wallet-card.purple .mem-wallet-label { color: #8b5cf6; }

  .mem-wallet-amount {
    font-size: clamp(18px, 2.8vw, 22px);
    font-weight: 900;
    color: #1e293b;
  }
  .mem-wallet-pending {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin-top: 6px;
    padding: 3px 10px;
    border-radius: 99px;
    background: linear-gradient(145deg, #fff3ea, #ffe4d6);
    font-size: 9px;
    font-weight: 700;
    color: #ea580c;
    box-shadow: inset 1px 1px 2px rgba(255,255,255,0.7), inset -1px -1px 2px rgba(0,0,0,0.05);
  }

  /* ══════════════════════════════
     CARDS — Neumorphism
  ══════════════════════════════ */
  .mem-card {
    background: linear-gradient(145deg, #e8edf5, #dce1e8);
    border-radius: 9px;
    padding: 20px;
    box-shadow: 12px 12px 24px #8f95a0, -6px -6px 14px #ffffff;
  }
  .mem-card-title {
    font-size: 13px;
    font-weight: 900;
    background: linear-gradient(135deg, #2563eb, #8b5cf6);
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    text-transform: uppercase;
    letter-spacing: 0.09em;
    margin-bottom: 22px;
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .mem-card-title-bar {
    width: 6px;
    height: 22px;
    border-radius: 99px;
    background: linear-gradient(180deg, #fbbf24, #f59e0b);
    box-shadow: 0 2px 8px rgba(245,158,11,0.4);
  }

  /* Income Grid */
  .mem-income-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
  }
  @media (max-width: 700px) { .mem-income-grid { grid-template-columns: 1fr; } }

  .mem-income-card {
    border-radius: 9px;
    padding: 16px;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    overflow: hidden;
    box-shadow: 8px 8px 16px #8f95a0, -6px -6px 12px #ffffff;
  }
  .mem-income-card:hover {
    transform: translateY(-3px);
    box-shadow: 12px 12px 24px #8f95a0, -8px -8px 14px #ffffff;
  }
  .mem-income-card.normal {
    background: linear-gradient(145deg, #e8edf5, #dce1e8);
  }
  .mem-income-card.normal.blue::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    border-radius: 22px 22px 0 0;
    background: linear-gradient(90deg, #3b82f6, #60a5fa);
  }
  .mem-income-card.normal.green::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    border-radius: 22px 22px 0 0;
    background: linear-gradient(90deg, #10b981, #34d399);
  }
  .mem-income-card.normal.blue .mem-income-icon {
    background: linear-gradient(145deg, #dbeafe, #eff6ff);
    color: #3b82f6;
  }
  .mem-income-card.normal.green .mem-income-icon {
    background: linear-gradient(145deg, #dcfce7, #f0fdf4);
    color: #10b981;
  }
  .mem-income-card.normal.blue .mem-income-label {
    color: #3b82f6;
  }
  .mem-income-card.normal.green .mem-income-label {
    color: #10b981;
  }
  .mem-income-card.highlight {
    background: linear-gradient(135deg, #0a1628, #1e3a5f, #2563eb);
    border: 1px solid rgba(255,255,255,0.16);
  }
  .mem-income-card.highlight::before {
    content: '';
    position: absolute;
    top: -30px;
    right: -30px;
    width: 120px;
    height: 120px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%);
  }
  .mem-income-label {
    font-size: 10px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    margin-bottom: 12px;
  }
  .mem-income-card.highlight .mem-income-label { color: rgba(255,255,255,0.8); }
  .mem-income-row {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
  }
  .mem-income-amount {
    font-size: clamp(22px, 3vw, 28px);
    font-weight: 900;
  }
  .mem-income-card.normal .mem-income-amount { color: #1e293b; }
  .mem-income-card.highlight .mem-income-amount { 
    color: #fbbf24; 
    text-shadow: 0 1px 2px rgba(0,0,0,0.1); 
  }
  .mem-income-icon {
    width: 44px;
    height: 44px;
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  .mem-income-card.highlight .mem-income-icon {
    background: rgba(255,255,255,0.15);
    backdrop-filter: blur(4px);
    box-shadow: inset -1px -1px 2px rgba(255,255,255,0.2), inset 1px 1px 2px rgba(0,0,0,0.1);
    color: #fbbf24;
  }

  /* KYC Status Cards */
  .mem-kyc-card {
    border-radius: 24px;
    padding: 20px 24px;
    box-shadow: 12px 12px 24px #8f95a0, -6px -6px 12px #ffffff;
  }
  .mem-kyc-card.rejected {
    background: linear-gradient(145deg, #fef2f2, #fee2e2);
    border: 1px solid #fecaca;
  }
  .mem-kyc-card.pending {
    background: linear-gradient(145deg, #fffbeb, #fef3c7);
    border: 1px solid #fde68a;
  }
  .mem-kyc-card.verified {
    background: linear-gradient(145deg, #f0fdf4, #dcfce7);
    border: 1px solid #bbf7d0;
  }
  .mem-kyc-icon {
    width: 48px;
    height: 48px;
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    box-shadow: 6px 6px 12px rgba(0,0,0,0.15), -4px -4px 8px rgba(255,255,255,0.3);
  }
  .mem-kyc-icon.rejected { background: linear-gradient(135deg, #dc2626, #ef4444); }
  .mem-kyc-icon.pending { background: linear-gradient(135deg, #d97706, #f59e0b); }
  .mem-kyc-icon.verified { background: linear-gradient(135deg, #059669, #10b981); }

  /* Royalty Button */
  .mem-royalty-btn {
    background: linear-gradient(135deg, #1e3a5f, #2563eb);
    color: #fff;
    font-weight: 800;
    font-family: 'Montserrat', sans-serif;
    border: none;
    border-radius: 60px;
    padding: 14px 28px;
    font-size: 14px;
    cursor: pointer;
    box-shadow: 10px 10px 20px #8f95a0, -4px -4px 10px #ffffff;
    transition: all 0.2s;
    width: 100%;
    max-width: 260px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }
  .mem-royalty-btn:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 14px 14px 24px #8f95a0, -6px -6px 14px #ffffff;
  }
  .mem-royalty-btn:active:not(:disabled) {
    transform: translateY(1px);
  }
  .mem-royalty-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  /* Empty State */
  .mem-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 48px 20px;
    text-align: center;
    background: linear-gradient(145deg, #f0f4f9, #e8edf5);
    border-radius: 20px;
    box-shadow: inset 3px 3px 8px rgba(0,0,0,0.05), inset -3px -3px 8px rgba(255,255,255,0.6);
  }
  .mem-empty-icon { font-size: 48px; margin-bottom: 16px; }
  .mem-empty-text {
    font-size: 14px;
    font-weight: 700;
    color: #94a3b8;
  }

  /* Message Alerts */
  .mem-success-msg {
    margin-top: 10px;
    color: #059669;
    font-weight: 700;
    font-size: 13px;
    display: flex;
    align-items: center;
    gap: 8px;
    background: linear-gradient(145deg, #ecfdf5, #d1fae5);
    padding: 10px 16px;
    border-radius: 12px;
    box-shadow: inset 1px 1px 2px rgba(255,255,255,0.7), inset -1px -1px 2px rgba(0,0,0,0.05);
  }
  .mem-error-msg {
    margin-top: 10px;
    color: #dc2626;
    font-weight: 700;
    font-size: 13px;
    display: flex;
    align-items: center;
    gap: 8px;
    background: linear-gradient(145deg, #fef2f2, #fee2e2);
    padding: 10px 16px;
    border-radius: 12px;
    box-shadow: inset 1px 1px 2px rgba(255,255,255,0.7), inset -1px -1px 2px rgba(0,0,0,0.05);
  }

  /* Skeleton Loading */
  .mem-skeleton {
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding: 20px;
    max-width: 1280px;
    margin: 0 auto;
  }
  .mem-skel {
    border-radius: 32px;
    background: #dce1e8;
    position: relative;
    overflow: hidden;
    box-shadow: 8px 8px 16px #8f95a0, -8px -8px 16px #ffffff;
  }
  .mem-skel::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, transparent 25%, rgba(255,255,255,0.5) 50%, transparent 75%);
    background-size: 200% 100%;
    animation: mem-shimmer 1.4s infinite;
  }
  @keyframes mem-shimmer {
    from { background-position: -200% 0; }
    to { background-position: 200% 0; }
  }
  .mem-skel-hero { height: 180px; }
  .mem-skel-row {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 20px;
  }
  @media (max-width: 900px) { .mem-skel-row { grid-template-columns: repeat(2, 1fr); } }
  @media (max-width: 500px) { .mem-skel-row { grid-template-columns: 1fr; } }
  .mem-skel-box {
    height: 130px;
    border-radius: 28px;
    background: #dce1e8;
    position: relative;
    overflow: hidden;
  }
  .mem-skel-box::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, transparent 25%, rgba(255,255,255,0.5) 50%, transparent 75%);
    background-size: 200% 100%;
    animation: mem-shimmer 1.4s infinite;
  }
  .mem-skel-card { height: 200px; }
`;

/* ═══════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════ */

const MemberDashboard = ({ user }) => {
  const [wallet, setWallet] = useState(null);
  const [income, setIncome] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchaseStatus, setPurchaseStatus] = useState({ hasSharePurchase: false, hasProductPurchase: false });
  const [royaltyLoading, setRoyaltyLoading] = useState(false);
  const [royaltyMsg, setRoyaltyMsg] = useState("");
  const [royaltyError, setRoyaltyError] = useState("");
  
  // Product Purchases states
  const [productPurchases, setProductPurchases] = useState([]);
  const [purchasesLoading, setPurchasesLoading] = useState(false);
  
  // Invoice modal states
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceData, setInvoiceData] = useState(null);
  const [loadingInvoice, setLoadingInvoice] = useState(false);

  const handleGenerateRoyalty = async () => {
    setRoyaltyMsg("");
    setRoyaltyError("");
    setRoyaltyLoading(true);
    try {
      const token = localStorage.getItem("token");
      const { data } = await axios.post(
        `${process.env.REACT_APP_API_URL}/share/generate-royalty`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRoyaltyMsg(data.message || "Royalty generated successfully!");
    } catch (err) {
      setRoyaltyError(
        err.response?.data?.message || "Failed to generate royalty."
      );
    } finally {
      setRoyaltyLoading(false);
    }
  };

  const fetchProductPurchases = async () => {
    setPurchasesLoading(true);
    try {
      const token = localStorage.getItem("token");
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_URL}/product/purchases`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setProductPurchases(data.purchases || []);
    } catch (err) {
      console.error("Failed to fetch product purchases:", err);
    } finally {
      setPurchasesLoading(false);
    }
  };

  const handleViewInvoice = async (orderId) => {
    setLoadingInvoice(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/product/invoice/${orderId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setInvoiceData(response.data.invoice);
      setShowInvoiceModal(true);
    } catch (err) {
      alert("Failed to fetch invoice: " + (err.response?.data?.message || err.message));
    } finally {
      setLoadingInvoice(false);
    }
  };

  const handlePrintInvoice = () => {
    window.print();
  };

  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem("token");
        const { data } = await axios.get(
          `${process.env.REACT_APP_API_URL}/dashboard/member`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setWallet(data.wallet);
        setIncome(data.income);
        setPurchaseStatus({
          hasSharePurchase: data.user.hasSharePurchase,
          hasProductPurchase: data.user.hasProductPurchase
        });

        // Fetch product purchases
        await fetchProductPurchases();
      } catch {
        // Handle error silently
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return (
    <div className="mem-root">
      <style>{styles}</style>
      <div className="mem-skeleton">
        <div className="mem-skel mem-skel-hero" />
        <div className="mem-skel-row">
          {[1, 2, 3].map(i => <div className="mem-skel-box" key={i} />)}
        </div>
        <div className="mem-skel mem-skel-card" />
        <div className="mem-skel mem-skel-card" />
      </div>
    </div>
  );

  const combinedIncome = income?.combinedIncome || 0;
  const totalWithdrawn = wallet?.totalWithdrawn || 0;
  const remainIncome = combinedIncome - totalWithdrawn;

  return (
    <div className="mem-root">
      <style>{styles}</style>
      <div className="mem-page">

        {/* ── Hero Section ── */}
        <div className="mem-hero">
          <div className="mem-hero-left">
            <div className="mem-hero-greeting">
              <FiShield size={12} style={{ display: 'inline', marginRight: 6 }} />
              Member Dashboard
            </div>
            <div className="mem-hero-name">Welcome, {user?.name || "Member"}!</div>
            <div className="mem-hero-sub">ID: {user?.uniqueId || "Member"} — Here's your financial overview</div>
            <div className="mem-hero-badges">
              {purchaseStatus.hasSharePurchase ? (
                <span className="mem-badge amber">
                  <span className="mem-badge-dot" /> LBBP Active
                </span>
              ) : (
                <span className="mem-badge amber2">
                  <span className="mem-badge-dot" /> LBBP Inactive
                </span>
              )}
              {purchaseStatus.hasProductPurchase ? (
                <span className="mem-badge amber">
                  <span className="mem-badge-dot" /> PU&AP Active
                </span>
              ) : (
                <span className="mem-badge amber2">
                  <span className="mem-badge-dot" /> PU&AP Inactive
                </span>
              )}
            </div>
          </div>
          <div className="mem-hero-stats">
            <div className="mem-hero-stat">
              <div className="mem-hero-stat-val">
                ₹{totalWithdrawn.toLocaleString("en-IN", { maximumFractionDigits: 0 })}
              </div>
              <div className="mem-hero-stat-label">Total Withdrawn</div>
            </div>
            <div className="mem-hero-stat">
              <div className="mem-hero-stat-val">
                ₹{combinedIncome.toLocaleString("en-IN")}
              </div>
              <div className="mem-hero-stat-label">Total Income</div>
            </div>
            <div className="mem-hero-stat">
              <div className="mem-hero-stat-val">
                ₹{remainIncome.toLocaleString("en-IN")}
              </div>
              <div className="mem-hero-stat-label">Remaining Balance</div>
            </div>
          </div>
        </div>

        {/* ── KYC Status Section ── */}
        {user?.kycStatus === 'rejected' && (
          <div className="mem-kyc-card rejected">
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
              <div className="mem-kyc-icon rejected">
                <FiX size={22} color="white" />
              </div>
              <div style={{ flex: 1 }}>
                <h4 style={{ margin: '0 0 6px 0', fontSize: 14, fontWeight: 800, color: '#991b1b', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  KYC Verification Rejected
                </h4>
                <div style={{ marginBottom: 6 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#7f1d1d', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                    Rejection Reason:
                  </span>
                </div>
                <p style={{
                  margin: 0,
                  fontSize: 13,
                  fontWeight: 600,
                  color: '#b91c1c',
                  lineHeight: 1.5,
                  padding: '10px 14px',
                  background: 'rgba(254, 202, 202, 0.5)',
                  borderRadius: 12,
                  border: '1px solid #fca5a5'
                }}>
                  {user?.kycRejectionReason || 'No reason provided'}
                </p>
                {user?.kycRejectionDate && (
                  <div style={{ marginTop: 10, fontSize: 11, color: '#7f1d1d', fontWeight: 700 }}>
                    Rejected on: {new Date(user.kycRejectionDate).toLocaleDateString('en-IN', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {user?.kycStatus === 'pending' && (
          <div className="mem-kyc-card pending">
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <div className="mem-kyc-icon pending">
                <FiClock size={22} color="white" />
              </div>
              <div>
                <h4 style={{ margin: 0, fontSize: 14, fontWeight: 800, color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  KYC Verification Pending
                </h4>
                <p style={{ margin: '6px 0 0 0', fontSize: 12, color: '#b45309', fontWeight: 600 }}>
                  Your KYC documents are under review. We'll notify you once verified.
                </p>
              </div>
            </div>
          </div>
        )}

        {user?.kycStatus === 'verified' && (
          <div className="mem-kyc-card verified">
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <div className="mem-kyc-icon verified">
                <FiCheck size={22} color="white" />
              </div>
              <div>
                <h4 style={{ margin: 0, fontSize: 14, fontWeight: 800, color: '#065f46', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  KYC Verified
                </h4>
                <p style={{ margin: '6px 0 0 0', fontSize: 12, color: '#047857', fontWeight: 600 }}>
                  Your KYC verification is complete and approved.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* ── Royalty Generation Button ── */}
        {/* <div style={{ marginTop: 6 }}>
          <button
            onClick={handleGenerateRoyalty}
            disabled={royaltyLoading}
            className="mem-royalty-btn"
          >
            {royaltyLoading ? (
              <><div className="mem-badge-dot" style={{ display: 'inline-block' }} /> Generating Royalty...</>
            ) : (
              <><FiAward size={16} /> Generate Royalty</>
            )}
          </button>
          {royaltyMsg && (
            <div className="mem-success-msg">
              <FiCheck size={14} /> {royaltyMsg}
            </div>
          )}
          {royaltyError && (
            <div className="mem-error-msg">
              <FiX size={14} /> {royaltyError}
            </div>
          )}
        </div> */}

        {/* ── Wallets Section ── */}
        <div className="mem-section-label">
          <FaWallet size={12} /> Wallets
        </div>
        <div className="mem-wallet-grid">
          {user?.isShareActive && (
            <WalletCard
              variant="blue"
              title="LBBP Wallet"
              balance={income?.totalShareIncome || 0}
              pending={wallet?.lbbpWithdrawalPending || 0}
            />
          )}
          {user?.isProductActive && (
            <WalletCard
              variant="green"
              title="PU&AP Wallet"
              balance={income?.totalProductIncome || 0}
              pending={wallet?.puapWithdrawalPending || 0}
            />
          )}
          <WalletCard
            variant="purple"
            title="E-Wallet"
            balance={wallet?.eWallet || 0}
          />
        </div>

        {/* ── Income Overview ── */}
        {(user?.isShareActive || user?.isProductActive) && (
          <>
            <div className="mem-section-label">
              <FiPieChart size={12} /> Income Overview
            </div>
            <div className="mem-card">
              <div className="mem-card-title">
                <div className="mem-card-title-bar" />
                Earnings Summary
              </div>
              <div className="mem-income-grid">
                <IncomeCard
                  title="LBBP Income"
                  amount={income?.totalShareIncome || 0}
                  icon={<FiTrendingUp size={18} />}
                  variant="blue"
                />
                <IncomeCard
                  title="PU&AP Income"
                  amount={income?.totalProductIncome || 0}
                  icon={<FiShoppingBag size={18} />}
                  variant="green"
                />
                <IncomeCard
                  title="Combined Income"
                  amount={income?.combinedIncome || 0}
                  icon={<FiPieChart size={18} />}
                  highlight
                />
              </div>
            </div>
          </>
        )}

        {/* ── PU & AP Services Section ── */}
        {user?.isProductActive && productPurchases.length > 0 && (
          <>
            <div className="mem-section-label">
              <FiShoppingBag size={12} /> PU & AP Services
            </div>
            <div className="mem-card">
              <div className="mem-card-title">
                <div className="mem-card-title-bar" />
                Your Product Purchases
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: '#f1f5f9', borderBottom: '2px solid #e2e8f0' }}>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: 700, color: '#475569' }}>Date</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: 700, color: '#475569' }}>Product</th>
                      <th style={{ padding: '12px', textAlign: 'right', fontWeight: 700, color: '#475569' }}>Qty</th>
                      <th style={{ padding: '12px', textAlign: 'right', fontWeight: 700, color: '#475569' }}>Amount</th>
                      <th style={{ padding: '12px', textAlign: 'right', fontWeight: 700, color: '#475569' }}>PV</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: 700, color: '#475569' }}>Status</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: 700, color: '#475569' }}>Invoice</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: 700, color: '#475569' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {productPurchases.map((purchase) => (
                      <tr key={purchase._id} style={{ borderBottom: '1px solid #e2e8f0', background: 'white' }}>
                        <td style={{ padding: '12px', color: '#64748b' }}>
                          {new Date(purchase.createdAt).toLocaleDateString("en-IN", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })}
                        </td>
                        <td style={{ padding: '12px' }}>
                          <div style={{ fontWeight: 600, color: '#1e293b' }}>
                            {purchase.productId?.name || "Unknown Product"}
                          </div>
                          <div style={{ fontSize: 11, color: '#64748b' }}>
                            ₹{(purchase.pricePerUnit || 0).toLocaleString("en-IN")}/unit
                          </div>
                        </td>
                        <td style={{ padding: '12px', textAlign: 'right', fontWeight: 600, color: '#1e293b' }}>
                          {purchase.quantity || 1}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'right', fontWeight: 700, color: '#2563eb' }}>
                          ₹{(purchase.totalAmount || 0).toLocaleString("en-IN")}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'right', fontWeight: 600, color: '#7c3aed' }}>
                          {purchase.pv || 0}
                        </td>
                        <td style={{ padding: '12px' }}>
                          <span style={{
                            padding: '4px 10px',
                            borderRadius: '99px',
                            fontSize: 10,
                            fontWeight: 700,
                            background: purchase.status === "completed" 
                              ? '#dcfce7' 
                              : purchase.status === "cancelled"
                              ? '#fee2e2'
                              : '#fef3c7',
                            color: purchase.status === "completed" 
                              ? '#166534' 
                              : purchase.status === "cancelled"
                              ? '#991b1b'
                              : '#92400e'
                          }}>
                            {(purchase.status || "pending").toUpperCase()}
                          </span>
                        </td>
                        <td style={{ padding: '12px' }}>
                          <div style={{ fontFamily: 'monospace', fontSize: 12, color: '#1e293b', fontWeight: 600 }}>
                            {purchase.invoiceNumber || (
                              <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>N/A</span>
                            )}
                          </div>
                        </td>
                        <td style={{ padding: '12px' }}>
                          {purchase.invoiceNumber ? (
                            <button
                              onClick={() => handleViewInvoice(purchase._id)}
                              disabled={loadingInvoice}
                              style={{
                                padding: '6px 12px',
                                background: '#2563eb',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                fontSize: 11,
                                fontWeight: 600,
                                cursor: loadingInvoice ? 'not-allowed' : 'pointer',
                                opacity: loadingInvoice ? 0.6 : 1,
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}
                            >
                              <FiEye /> View Invoice
                            </button>
                          ) : (
                            <span style={{ color: '#94a3b8', fontSize: 11, fontStyle: 'italic' }}>No Invoice</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

      </div>
      
      {/* Invoice Modal */}
      {showInvoiceModal && invoiceData && (
        <Invoice
          invoice={invoiceData}
          onClose={() => {
            setShowInvoiceModal(false);
            setInvoiceData(null);
          }}
          onPrint={handlePrintInvoice}
        />
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════
   SUB-COMPONENTS
═══════════════════════════════════════════ */

const WalletCard = ({ variant, title, balance, pending }) => {
  const getIcon = () => {
    switch (variant) {
      case 'blue':
        return <GiMoneyStack size={26} />;
      case 'green':
        return <GiTakeMyMoney size={26} />;
      case 'purple':
        return <MdAccountBalanceWallet size={26} />;
      default:
        return <FaWallet size={22} />;
    }
  };

  return (
    <div className={`mem-wallet-card ${variant}`}>
      <div className="mem-wallet-top">
        <div>
          <div className="mem-wallet-label">{title}</div>
          <div className="mem-wallet-amount">
            ₹{balance.toLocaleString("en-IN")}
          </div>
          {pending > 0 && (
            <div className="mem-wallet-pending">
              <FiClock size={12} />
              Pending: ₹{pending.toLocaleString("en-IN")}
            </div>
          )}
        </div>
        <div className="mem-wallet-icon-wrap">
          {getIcon()}
        </div>
      </div>
    </div>
  );
};

const IncomeCard = ({ title, amount, icon, highlight, variant }) => (
  <div className={`mem-income-card ${highlight ? "highlight" : "normal"} ${variant || ""}`}>
    <div className="mem-income-label">{title}</div>
    <div className="mem-income-row">
      <div className="mem-income-amount">
        ₹{amount.toLocaleString("en-IN")}
      </div>
      <div className="mem-income-icon">{icon}</div>
    </div>
  </div>
);

export default MemberDashboard;